import { API_ROOT, ApiError, http, SPATIAL_ROOT, type Fetch, type Http } from './http';
import type { DoorOrderSpec, DoorPricing, DoorQuote } from './doorPricing';

/**
 * The live door order: the server prices a spec and adds it to the basket.
 *
 * <p>Both calls send the whole spec and nothing else. The server finds the
 * standard piece each row is cut from, prices it through the shop's own price
 * rules and adds it; a price, SKU or product id from the browser would be
 * ignored, so none is ever sent (DOOR_AND_CARCASS_BUILDERS_PLAN.md F1–F3).
 */

/**
 * Where the storefront keeps a shopper's basket token. The site-builder
 * runtime's cart state owns it (`portal/cartState.tsx`, `TOKEN_STORAGE_KEY`);
 * the door order has to add to that same basket, so it reads and writes the
 * same key, and the block then asks the cart state to re-read it.
 */
export const CART_TOKEN_KEY = 'sorsy.cart.token';

/**
 * A refusal from the server already carries its first violation as the message
 * ("the largest 715 mm door is 597 mm wide"), so it is passed on as it is.
 */
export function explain(error: unknown): never {
  if (error instanceof ApiError) throw error;
  throw new Error('The shop could not be reached. Please try again.');
}

export function liveDoorPricing(fetchImpl?: Fetch): DoorPricing {
  const api = http(fetchImpl);
  return {
    quote: (spec) => api.send<DoorQuote>('POST', `${SPATIAL_ROOT}/kitchen/door-orders/quote`, spec).catch(explain),
  };
}

export interface CartTokenStore {
  read(): string | null;
  write(token: string): void;
  clear(): void;
}

export const browserCartTokens: CartTokenStore = {
  read() {
    try {
      return window.localStorage.getItem(CART_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  write(token) {
    try {
      window.localStorage.setItem(CART_TOKEN_KEY, token);
    } catch {
      // Private mode: the basket still exists server-side for this page view.
    }
  },
  clear() {
    try {
      window.localStorage.removeItem(CART_TOKEN_KEY);
    } catch {
      // Nothing held, nothing to forget.
    }
  },
};

/**
 * Runs `call` against the shopper's basket, creating one when none is held.
 * A held basket that was checked out or expired answers 404: the token is
 * dropped and `call` runs once more on a fresh basket.
 */
export async function withBasket(
  api: Http,
  tokens: CartTokenStore,
  call: (token: string) => Promise<{ token?: string } | null | undefined>,
): Promise<void> {
  async function basketToken(): Promise<string> {
    const held = tokens.read();
    if (held) return held;
    const created = await api.send<{ token: string }>('POST', `${API_ROOT}/cart`, {});
    tokens.write(created.token);
    return created.token;
  }
  for (let attempt = 0; ; attempt++) {
    const cartToken = await basketToken();
    try {
      const cart = await call(cartToken);
      if (cart?.token) tokens.write(cart.token);
      return;
    } catch (error) {
      if (attempt === 0 && error instanceof ApiError && error.status === 404) {
        tokens.clear();
        continue;
      }
      explain(error);
    }
  }
}

export interface DoorBasketAdapter {
  add(spec: DoorOrderSpec): Promise<void>;
}

export function liveDoorBasket(fetchImpl?: Fetch, tokens: CartTokenStore = browserCartTokens): DoorBasketAdapter {
  const api = http(fetchImpl);
  return {
    // The basket token travels in the path, as it does on the cart routes.
    add: (spec) =>
      withBasket(api, tokens, (cartToken) =>
        api.send<{ token?: string }>('POST', `${SPATIAL_ROOT}/kitchen/door-orders/basket/${encodeURIComponent(cartToken)}`, spec),
      ),
  };
}
